import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import {
  Compass,
  Heart,
  HelpCircle,
  CircleDollarSign,
  Smartphone,
} from 'lucide-react';

import { MEDIA } from '../../constants/media';

// Import constants
import { SUPPORT_US_URL } from '../../constants/links';

// Import utils
import { scrollToId } from '../../utils/scroll';

// Import hooks
import useHashLinkClick from '../../hooks/ui/useHashLinkClick';

// Import components
import AppQRPopup from './AppQRPopup';

export default function Navbar() {
  const location = useLocation();
  const [showQRPopup, setShowQRPopup] = useState(false);
  const onHashLinkClick = useHashLinkClick();

  const handleLogoClick = () => {
    // If we're on the landing page, scroll to Hero instead of navigating
    if (location.pathname === '/') {
      // Enable the link navigation to replace the path with "/"
      // e.preventDefault();
      scrollToId('hero');
    }
    // Otherwise, let the Link navigate normally to "/"
  };

  // prettier-ignore
  const navClasses = 'flex items-center justify-between max-w-6xl mx-auto h-16 transition-shadow';

  return (
    <nav className={navClasses}>
      {/* Logo */}
      <Link
        to="/"
        className="flex items-center gap-3 hover:underline"
        onClick={handleLogoClick}>
        <img
          src={MEDIA.logo}
          alt="WhenWeMeet"
          className="h-10 w-10 rounded-2xl shadow-sm shadow-gray-300 object-cover hover:shadow-md transition-shadow"
          loading="lazy"
          decoding="async"
        />
        <span className="text-2xl font-semibold text-gray-900">
          WhenWeMeet?
        </span>
      </Link>

      {/* links shown on md+ */}
      <div className="hidden md:flex items-center gap-2">
        {/* How it works */}
        <Link
          to="/#how-to-use"
          className="flex gap-2 rounded-full px-2 lg:px-4 py-2 hover:bg-slate-200"
          onClick={(e) => onHashLinkClick(e, 'how-to-use')}>
          <Compass className="w-6 h-6 hidden lg:block" />
          <span className="text-nowrap text-sm md:text-base font-semibold text-slate-800">
            How it works
          </span>
        </Link>

        {/* Pricing */}
        <Link
          to="/#pricing"
          className="flex gap-2 rounded-full px-2 lg:px-4 py-2 hover:bg-slate-200"
          onClick={(e) => onHashLinkClick(e, 'pricing')}>
          <CircleDollarSign className="w-6 h-6 hidden lg:block" />
          <span className="text-nowrap text-sm md:text-base font-semibold text-slate-800">
            Pricing
          </span>
        </Link>

        {/* Support */}
        <Link
          to="/support"
          className="flex gap-2 rounded-full px-2 lg:px-4 py-2 hover:bg-slate-200">
          <HelpCircle className="w-6 h-6 hidden lg:block" />
          <span className="text-nowrap text-sm md:text-base font-semibold text-slate-800">
            Support
          </span>
        </Link>

        {/* Support us */}
        <a
          href={SUPPORT_US_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex gap-2 rounded-full bg-green-700 text-nowrap text-white px-5 py-2 text-sm sm:text-base font-semibold border border-gray-200/20 hover:bg-green-800 hover:shadow-md transition-shadow">
          <Heart className="w-6 h-6 hidden lg:block" />
          <span>Support us</span>
        </a>

        {/* App */}
        <div
          className="relative"
          onMouseEnter={() => setShowQRPopup(true)}
          onMouseLeave={() => setShowQRPopup(false)}>
          <Link
            to="/app"
            className="flex gap-2 rounded-full bg-gray-800 text-white px-2.5 py-2 border border-gray-200/20 hover:bg-gray-900 hover:shadow-md transition-shadow">
            <Smartphone className="w-6 h-6 hidden md:block" />
          </Link>
          <AppQRPopup show={showQRPopup} />
        </div>
      </div>

      {/* Mobile placeholder to avoid overflow; links shown on the smaller screen */}
      <div className="flex flex-row md:hidden">
        {/* App */}
        <Link
          to="/app"
          className="gap-2 rounded-full bg-gray-800 text-white px-2.5 py-2 border border-gray-200/20 hover:bg-gray-900 hover:shadow-md transition-shadow">
          <Smartphone className="w-6 h-6" />
        </Link>
      </div>
    </nav>
  );
}
